import React, {
  KeyboardEvent,
  useEffect,
  useState,
  useMemo,
  FunctionComponent,
} from 'react';
import PropTypes from 'prop-types';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { debounce, range } from 'lodash';
import './Input.scss';
import CachedInput from './CachedInput';

interface SliderInputProps {
  value?: number;
  max?: number;
  disabled?: boolean;
  onChange?: (value: string | number) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

const SliderInput: FunctionComponent<SliderInputProps> = ({
  value = 0,
  max = 100,
  disabled,
  onChange = () => {},
  onKeyDown,
}) => {
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => setLocalValue(value), [value]);
  const onChangeDebounced = useMemo(() => debounce(onChange, 300), [onChange]);
  const setValue = (newValue: number) => {
    setLocalValue(Math.max(Math.min(newValue, max), 0));
    onChangeDebounced(newValue);
  };

  const onChangeHandler = (newValue: string | boolean) => {
    if (
      typeof newValue !== 'boolean' &&
      newValue.trim() !== '' &&
      !isNaN(Number(newValue))
    ) {
      setValue(Number(newValue));
    }
  };

  // snap to every $50, as well as the max value
  const marks = useMemo(
    () => ({
      ...range(0, max, 50).reduce(
        (prev, curr) => ({ ...prev, [curr]: '' }),
        {},
      ),
      [max]: '',
    }),
    [max],
  );

  return (
    <div className={`slider-input ${disabled ? 'slider-input-disabled' : ''}`}>
      <div className="slider-min-mark">0</div>
      <div className="slider-max-mark">{max}</div>
      <div
        className="slider-handle-input-container"
        style={{
          left: `${max ? (localValue / max) * 100 : 0}%`,
        }}
      >
        <CachedInput
          className="slider-handle-input"
          type="number"
          value={localValue}
          onChange={onChangeHandler}
          disabled={disabled}
          onKeyDown={onKeyDown}
          placeholder={'0'}
        />
        <div className="slider-handle-input-arrow">&#x25BC;</div>
      </div>
      <Slider
        min={0}
        max={max || 1}
        marks={marks}
        step={null}
        value={localValue}
        onChange={setLocalValue}
        onAfterChange={setValue}
        disabled={disabled}
      />
    </div>
  );
};

SliderInput.propTypes = {
  value: PropTypes.number,
  max: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
};

export default SliderInput;
